import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package,
  MapPin,
  Heart,
  User,
  ArrowRight,
  ShoppingBag,
  Clock,
  CheckCircle,
  Truck,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useOrders } from '@/hooks/useProducts';
import { formatCurrency, formatDateShort } from '@/lib/utils';
import { IS_SAAS } from '@/config/site';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import SaasDashboard from './SaasDashboard';

type OrderStatus =
  | 'pending'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

const statusConfig: Record<
  OrderStatus,
  { variant: 'green' | 'yellow' | 'red' | 'blue' | 'gray' }
> = {
  pending: { variant: 'yellow' },
  processing: { variant: 'blue' },
  shipped: { variant: 'blue' },
  delivered: { variant: 'green' },
  cancelled: { variant: 'red' },
  refunded: { variant: 'gray' },
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('account');
  const config = statusConfig[status as OrderStatus] || { variant: 'gray' as const };
  const key = `orders.statuses.${status}` as const;
  const translated = t(key);
  const label = translated === key ? status : translated;
  return <Badge variant={config.variant}>{label}</Badge>;
}

export default function Dashboard() {
  // SaaS tenants have no shop/orders — render the account-management home
  // instead of the ecommerce order dashboard. (Hooks below are unconditional
  // for ecommerce; the early return keeps SaaS off the orders query path.)
  if (IS_SAAS) return <SaasDashboard />;

  return <EcommerceDashboard />;
}

function EcommerceDashboard() {
  const { t } = useTranslation('account');
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useOrders(1);

  const orders = data?.orders ?? [];
  const recentOrders = orders.slice(0, 3);
  const totalOrders = data?.total ?? 0;

  const deliveredCount = orders.filter(
    (o: any) => o.status === 'delivered'
  ).length;
  const activeCount = orders.filter((o: any) =>
    ['pending', 'processing', 'shipped'].includes(o.status)
  ).length;

  const quickLinks = [
    {
      label: t('dashboard.quickLinks.addresses.label'),
      description: t('dashboard.quickLinks.addresses.description'),
      href: '/account/addresses',
      icon: MapPin,
    },
    {
      label: t('dashboard.quickLinks.wishlist.label'),
      description: t('dashboard.quickLinks.wishlist.description'),
      href: '/account/wishlist',
      icon: Heart,
    },
    {
      label: t('dashboard.quickLinks.profile.label'),
      description: t('dashboard.quickLinks.profile.description'),
      href: '/account/profile',
      icon: User,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <section aria-label={t('dashboard.welcomeAriaLabel')}>
        <h2 className="text-xl font-display font-bold text-ink">
          {user?.firstName
            ? t('dashboard.welcomeWithName', { name: user.firstName })
            : t('dashboard.welcomeFallback')}
        </h2>
        <p className="text-ink-muted mt-1">
          {t('dashboard.subtitle')}
        </p>
      </section>

      {/* Order Stats */}
      <section aria-label={t('dashboard.statsAriaLabel')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <Package className="h-5 w-5 text-blue-700" aria-hidden="true" />
              </div>
              <div>
                <p className="text-2xl font-bold text-ink">{totalOrders}</p>
                <p className="text-sm text-ink-muted">{t('dashboard.stats.totalOrders')}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-700" aria-hidden="true" />
              </div>
              <div>
                <p className="text-2xl font-bold text-ink">{activeCount}</p>
                <p className="text-sm text-ink-muted">{t('dashboard.stats.activeOrders')}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                <CheckCircle className="h-5 w-5 text-success-700" aria-hidden="true" />
              </div>
              <div>
                <p className="text-2xl font-bold text-ink">{deliveredCount}</p>
                <p className="text-sm text-ink-muted">{t('dashboard.stats.delivered')}</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Recent Orders */}
      <section aria-label={t('dashboard.recentOrdersAriaLabel')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink">{t('dashboard.recentOrders')}</h3>
          <Link
            to="/account/orders"
            className="text-sm font-medium text-sage-700 hover:text-sage-800 inline-flex items-center gap-1"
          >
            {t('dashboard.viewAll')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : recentOrders.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <ShoppingBag
                className="h-12 w-12 text-ink-muted mx-auto mb-3"
                aria-hidden="true"
              />
              <p className="text-ink-muted mb-4">{t('dashboard.noOrders')}</p>
              <Link to="/shop">
                <Button>{t('common.startShopping')}</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {recentOrders.map((order: any) => {
              const itemCount = order.item_count ?? order.items?.length ?? 0;
              return (
              <Link
                key={order.id}
                to={`/account/orders/${order.id}`}
                className="block bg-surface rounded-lg border border-line p-4 hover:border-sage-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-ink-muted shrink-0" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-ink">
                        {t('orders.orderNumber', { number: order.order_number })}
                      </p>
                      <p className="text-sm text-ink-muted">
                        {formatDateShort(order.created_at)} &middot;{' '}
                        {t('common.itemCount', { count: itemCount })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:text-right">
                    <StatusBadge status={order.status} />
                    <span className="font-semibold text-ink">
                      {formatCurrency(Number(order.total))}
                    </span>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Quick Links */}
      <section aria-label={t('dashboard.quickLinksAriaLabel')}>
        <h3 className="text-lg font-semibold text-ink mb-4">{t('dashboard.quickLinksHeading')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {quickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-start gap-3 bg-surface rounded-lg border border-line p-4 hover:border-sage-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-muted shrink-0">
                  <Icon className="h-5 w-5 text-ink-soft" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-medium text-ink">{link.label}</p>
                  <p className="text-sm text-ink-muted">{link.description}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
